import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthAction, User, UserRole } from '../types/Auth';
import { getProfile } from '../api/auth';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
    sub: string; // Subject (user id)
    email: string;
    roles: UserRole[];
    company_id?: string;
    exp: number;
}

const initialState: AuthState = {
    accessToken: null,
    refreshToken: null,
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case 'LOGIN_SUCCESS':
            try {
                const decoded = jwtDecode<DecodedToken>(action.payload.accessToken);
                const user: User = {
                    id: decoded.sub,
                    email: decoded.email,
                    roles: decoded.roles,
                    company_id: decoded.company_id,
                    name: '' // Will be filled in by a subsequent profile fetch
                };
                return {
                    ...state,
                    isAuthenticated: true,
                    isLoading: false,
                    accessToken: action.payload.accessToken,
                    refreshToken: action.payload.refreshToken,
                    user: user,
                    error: null,
                };
            } catch (error) {
                console.error("Invalid token", error);
                return { ...initialState, isLoading: false };
            }
        case 'LOGOUT':
            return { ...initialState, isLoading: false };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, isLoading: false, error: action.payload };
        case 'SET_USER':
            if (!state.user) return state; // Should not happen if called after LOGIN_SUCCESS
            return { ...state, user: { ...state.user, ...action.payload.user } };
        default:
            return state;
    }
};

export const AuthContext = createContext<{ state: AuthState; dispatch: React.Dispatch<AuthAction> } | undefined>(
    undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        const initAuth = async () => {
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');

            if (accessToken && refreshToken) {
                dispatch({ type: 'LOGIN_SUCCESS', payload: { accessToken, refreshToken } });
                try {
                    const profile = await getProfile();
                    dispatch({ type: 'SET_USER', payload: { user: profile } });
                } catch (error) {
                    console.warn("Could not fetch full user profile. Proceeding with basic info from token.", error);
                }
            } else {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };
        initAuth();
    }, []);

    return (
        <AuthContext.Provider value={{ state, dispatch }}>
            {children}
        </AuthContext.Provider>
    );
};
